import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Cart, CartModification, CartModificationList } from '@spartacus/cart/base/root';
import {
  GlobalMessageService,
  GlobalMessageType,
  OCC_USER_ID_ANONYMOUS,
  ProductSearchPage,
  User,
  UserIdService,
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  of,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { GenAutoShipOrderEntry, GenAutoShipQuantityChange } from '../../../models/auto-ship.model';
import { GenMSRPPrice } from '../../../models/product.model';
import { GenAutoShipCartService } from '../../../services/auto-ship-cart.service';
import { GenAutoShipProductsService } from '../../../services/auto-ship-products.service';

interface GenAutoShipRouteState {
  query: string;
  currentPage: number;
  sort: string;
}

interface GenAutoShipViewModel {
  model?: ProductSearchPage;
  cartQuantities: ReadonlyMap<string, number>;
  netPrices: ReadonlyMap<string, GenMSRPPrice>;
  netPricesLoading: boolean;
  loading: boolean;
  error: boolean;
}

interface GenAutoShipCartState {
  quantities: ReadonlyMap<string, number>;
  loading: boolean;
  error: boolean;
}

@Component({
  selector: 'gen-auto-ship-products',
  templateUrl: './auto-ship-products.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GenAutoShipProductsComponent implements OnDestroy {
  static readonly DEFAULT_QUERY = ':relevance:allCategories:autoship';
  static readonly PAGE_SIZE = 12;
  static readonly NEXT_STEP_URL = '/auto-ship/shipment-schedule';

  private readonly destroy$ = new Subject<void>();
  private readonly viewportVisible$ = new BehaviorSubject(false);
  private readonly canProceed$ = new BehaviorSubject(false);
  private readonly saving$ = new BehaviorSubject(false);
  private cartQuantities = new Map<string, number>();
  private selectedQuantities = new Map<string, number>();
  private userId?: string;

  readonly addToCartDisabled$ = combineLatest([
    this.canProceed$,
    this.saving$,
  ]).pipe(
    map(([canProceed, saving]) => !canProceed || saving),
  );

  private readonly autoShipCartState$: Observable<GenAutoShipCartState> = this.userIdService.getUserId().pipe(
    filter((userId) => !!userId && userId !== OCC_USER_ID_ANONYMOUS),
    take(1),
    switchMap((userId) => this.autoShipCartService.load(userId).pipe(
      tap((cart) => {
        this.userId = userId;
        this.resetQuantitiesFromCart(cart);
      }),
      map(() => ({
        quantities: this.cartQuantities,
        loading: false,
        error: false,
      })),
      catchError(() => of({
        quantities: new Map<string, number>(),
        loading: false,
        error: true,
      })),
      startWith({
        quantities: new Map<string, number>(),
        loading: true,
        error: false,
      }),
    )),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  readonly vm$: Observable<GenAutoShipViewModel> = combineLatest([
    this.viewportVisible$,
    this.route.queryParams.pipe(
      map((params) => this.readRouteState(params)),
      distinctUntilChanged((a, b) =>
        a.query === b.query && a.currentPage === b.currentPage && a.sort === b.sort
      ),
    ),
    this.autoShipCartState$,
  ]).pipe(
    switchMap(([visible, state, cartState]) => cartState.loading || cartState.error
      ? of({
        cartQuantities: cartState.quantities,
        netPrices: new Map<string, GenMSRPPrice>(),
        netPricesLoading: false,
        loading: cartState.loading,
        error: cartState.error,
      })
      : visible
        ? this.loadPage(state, cartState.quantities)
      : of({
        cartQuantities: cartState.quantities,
        netPrices: new Map<string, GenMSRPPrice>(),
        netPricesLoading: false,
        loading: false,
        error: false,
      })
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private autoShipProductsService: GenAutoShipProductsService,
    private autoShipCartService: GenAutoShipCartService,
    private userIdService: UserIdService,
    private userAccount: UserAccountFacade,
    private globalMessageService: GlobalMessageService,
  ) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onViewportVisible(): void {
    const state = this.readRouteState(this.route.snapshot.queryParams);
    const params = this.route.snapshot.queryParams;
    if (
      params.query !== state.query ||
      Number(params.currentPage) !== state.currentPage ||
      params.sort !== state.sort
    ) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: state,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    this.viewportVisible$.next(true);
  }

  changePage(currentPage: number): void {
    this.navigate({ currentPage });
  }

  onQuantityChange(change: GenAutoShipQuantityChange): void {
    if (!change.productCode) {
      return;
    }
    this.selectedQuantities.set(change.productCode, change.quantity);
    this.updateCanProceed();
  }

  addToCart(): void {
    const orderEntries = this.getChangedEntries();
    const userId = this.userId;
    if (!userId || !this.canProceed$.value || this.saving$.value) {
      return;
    }

    if (!orderEntries.length) {
      void this.router.navigateByUrl(GenAutoShipProductsComponent.NEXT_STEP_URL);
      return;
    }

    this.saving$.next(true);
    this.autoShipCartService.updateEntries(userId, orderEntries).pipe(
      tap((response) => this.showCartModificationMessages(response)),
      switchMap(() => this.autoShipCartService.load(userId)),
      finalize(() => this.saving$.next(false)),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (cart) => {
        this.resetQuantitiesFromCart(cart);
        void this.router.navigateByUrl(GenAutoShipProductsComponent.NEXT_STEP_URL);
      },
      error: (error: unknown) => this.showRequestError(error),
    });
  }

  private loadPage(
    state: GenAutoShipRouteState,
    cartQuantities: ReadonlyMap<string, number>,
  ): Observable<GenAutoShipViewModel> {
    return this.autoShipProductsService
      .search(state.query, state.currentPage, GenAutoShipProductsComponent.PAGE_SIZE)
      .pipe(
        switchMap((model) => {
          const initial: GenAutoShipViewModel = {
            model,
            cartQuantities,
            netPrices: new Map(),
            netPricesLoading: !!model.products?.length,
            loading: false,
            error: false,
          };
          const codes = (model.products ?? [])
            .map((product) => product.code)
            .filter((code): code is string => !!code);

          if (!codes.length) {
            return of(initial);
          }

          return this.userAccount.get().pipe(
            filter((user: User) => !!user?.uid),
            take(1),
            switchMap((user) => this.autoShipProductsService.getNetPrices(user.uid, codes)),
            map((response) => ({
              ...initial,
              netPrices: new Map<string, GenMSRPPrice>(
                (response.products ?? [])
                  .filter((price): price is GenMSRPPrice & { code: string } => !!price.code)
                  .map((price) => [price.code, price])
              ),
              netPricesLoading: false,
            })),
            catchError(() => of({ ...initial, netPricesLoading: false })),
            startWith(initial),
          );
        }),
        startWith({
          cartQuantities,
          netPrices: new Map<string, GenMSRPPrice>(),
          netPricesLoading: false,
          loading: true,
          error: false,
        }),
        catchError(() => of({
          cartQuantities,
          netPrices: new Map<string, GenMSRPPrice>(),
          netPricesLoading: false,
          loading: false,
          error: true,
        })),
      );
  }

  private getChangedEntries(): GenAutoShipOrderEntry[] {
    return Array.from(this.selectedQuantities.entries())
      .filter(([productCode, quantity]) =>
        quantity !== (this.cartQuantities.get(productCode) ?? 0)
      )
      .map(([productCode, quantity]) => ({
        product: { code: productCode },
        quantity,
      }));
  }

  private resetQuantitiesFromCart(cart: Cart): void {
    const quantities = new Map<string, number>();
    (cart.entries ?? []).forEach((entry) => {
      const productCode = entry.product?.code;
      if (productCode) {
        quantities.set(productCode, Number(entry.quantity) || 0);
      }
    });

    this.cartQuantities = quantities;
    this.selectedQuantities = new Map(quantities);
    this.updateCanProceed();
  }

  private updateCanProceed(): void {
    const hasSelectedQuantity = Array.from(this.selectedQuantities.values())
      .some((quantity) => quantity > 0);
    this.canProceed$.next(hasSelectedQuantity);
  }

  private showCartModificationMessages(response: CartModificationList): void {
    (response.cartModifications ?? [])
      .filter((modification) => modification.statusCode?.toLowerCase() !== 'success')
      .forEach((modification) => this.showCartModificationMessage(modification));
  }

  private showCartModificationMessage(modification: CartModification): void {
    const message = modification.statusMessage || {
      key: `validation.${modification.statusCode}`,
      params: {
        quantity: modification.quantityAdded ?? modification.quantity,
      },
    };
    this.globalMessageService.add(message, GlobalMessageType.MSG_TYPE_WARNING);
  }

  private showRequestError(error: unknown): void {
    const response = error as HttpErrorResponse;
    const notifications = response.error?.generacNotifications;
    if (Array.isArray(notifications) && notifications.some(
      (notification: { message?: string }) => !!notification.message
    )) {
      // GenNotificationResponseInterceptor displays backend notifications with
      // their supplied message level, so do not replace them with the OCC error.
      return;
    }

    const message = response.error?.errors?.[0]?.message
      || response.error?.message
      || (typeof response.error === 'string' ? response.error : undefined)
      || response.message;

    this.globalMessageService.add(
      message || { key: 'autoShip.products.updateError' },
      GlobalMessageType.MSG_TYPE_ERROR,
    );
  }

  private readRouteState(params: Params): GenAutoShipRouteState {
    const queryWithMarker = this.ensureAutoShipFilter(params.query || GenAutoShipProductsComponent.DEFAULT_QUERY);
    const requestedSort = params.sort || this.getSort(queryWithMarker);
    const query = this.withSort(queryWithMarker, requestedSort);
    const parsedPage = Number(params.currentPage);

    return {
      query,
      sort: requestedSort,
      currentPage: Number.isInteger(parsedPage) && parsedPage >= 0 ? parsedPage : 0,
    };
  }

  private ensureAutoShipFilter(query: string): string {
    return /(^|:)allCategories:autoship(?=:|$)/.test(query)
      ? query
      : `${query.replace(/:+$/, '')}:allCategories:autoship`;
  }

  private withSort(query: string, sort: string): string {
    const safeQuery = this.ensureAutoShipFilter(query);
    return safeQuery.startsWith(':')
      ? safeQuery.replace(/^:[^:]*/, `:${sort}`)
      : `:${sort}:${safeQuery.replace(/^[^:]*:?/, '')}`;
  }

  private getSort(query: string): string {
    return query.startsWith(':') ? query.split(':')[1] || 'relevance' : 'relevance';
  }

  private navigate(queryParams: Params): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
